import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BackLink {
  label: string;
  href: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  backLink?: BackLink;
  className?: string;
}

/**
 * PageHeader - Consistent header for all pages
 * 
 * Provides:
 * - Title with optional description
 * - Action buttons (right-aligned on desktop, stacked on mobile)
 * - Optional back navigation link
 * - Responsive layout that adapts to screen size
 * 
 * @example
 * <PageHeader 
 *   title="Create Estimate" 
 *   description="Fill in the details for your new estimate"
 *   backLink={{ label: "Back to Estimates", href: "/estimates" }}
 *   actions={<Button>Save</Button>}
 * />
 */
const PageHeader = ({
  title,
  description,
  actions,
  backLink,
  className,
}: PageHeaderProps) => {
  return (
    <div className={cn("space-y-4 mb-6", className)}>
      {/* Back link */}
      {backLink && (
        <Link
          to={backLink.href}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLink.label}
        </Link>
      )}

      {/* Title and actions row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1 min-w-0 flex-1">
          <h1 className="text-2xl font-semibold font-montserrat text-foreground truncate">
            {title}
          </h1>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>

        {/* Actions - stacks on mobile, inline on desktop */}
        {actions && (
          <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
